import { z } from "zod";
import { logicRulesSchema } from "../logic/types.js";

/**
 * Form builder request shapes — docs/03-API-SPEC.md §5. Imported by apps/api for validation and
 * by apps/dashboard for its builder forms: one definition, no drift.
 */

/**
 * Field `key` is the export column header and the analytics join key, and **never changes once
 * published** (docs/01-PRODUCT-SPEC.md §3.1). snake_case is enforced here so a key can be used
 * unquoted in a CSV header, a SQL identifier and a formula expression alike.
 */
export const fieldKeySchema = z
  .string()
  .min(1)
  .max(64)
  .regex(
    /^[a-z][a-z0-9_]*$/,
    "Use lowercase letters, numbers and underscores, starting with a letter",
  );

export const sectionKeySchema = fieldKeySchema;

/**
 * One field in the whole-schema save.
 *
 * The optional strings are `.nullish()`, not `.optional()`, and that is load-bearing: the database
 * columns are nullable, `GET /forms/:id/draft` serialises them straight out as `null`, and the
 * builder hands back what it was given. Accepting only `undefined` meant the API rejected its own
 * output — a form whose rows were written by anything other than the builder (a duplicate, a seed
 * script, a legacy import) could be opened and never saved.
 */
export const draftFieldSchema = z.object({
  key: fieldKeySchema,
  type: z.string().min(1),
  label: z.string().min(1).max(500),
  helpText: z.string().max(2000).nullish(),
  placeholder: z.string().max(300).nullish(),
  position: z.number().int().nonnegative(),
  rowGroup: z.number().int().nonnegative().default(0),
  widthSpan: z.number().int().min(1).max(12).default(12),
  isRequired: z.boolean().default(false),
  isReadOnly: z.boolean().default(false),
  visibility: z.enum(["visible", "hidden", "admin_only"]).default("visible"),
  profileAttributeKey: z.string().nullable().optional(),
  isPii: z.boolean().default(false),
  isSensitive: z.boolean().default(false),
  isSearchable: z.boolean().default(true),
  isDedupeKey: z.boolean().default(false),
  defaultValue: z.unknown().optional(),
  config: z.record(z.unknown()).default({}),
  validation: z.record(z.unknown()).default({}),
  sectionKey: sectionKeySchema.nullable().optional(),
});

export const draftSectionSchema = z.object({
  key: sectionKeySchema,
  title: z.string().max(300).nullish(),
  description: z.string().max(2000).nullish(),
  pageNumber: z.number().int().min(1).default(1),
  position: z.number().int().nonnegative(),
  collapsible: z.boolean().default(false),
});

/**
 * The whole-schema save. docs/03-API-SPEC.md §5: "Whole-schema save. Sections, fields and logic in
 * one payload — simpler and more reliable than per-field CRUD, and makes the builder's undo
 * trivial." Applied transactionally: the draft version's sections and fields are replaced wholesale.
 */
export const draftSaveRequestSchema = z.object({
  sections: z.array(draftSectionSchema).default([]),
  fields: z.array(draftFieldSchema).default([]),
  logic: logicRulesSchema.default([]),
  privacyNotice: z.string().max(100_000).nullable().optional(),
  settings: z.record(z.unknown()).default({}),
});

export const formCreateRequestSchema = z.object({
  programmeId: z.string().min(1),
  cycleId: z.string().min(1).nullable().optional(),
  // Trimmed before the length check, not after: `min(1)` on an untrimmed string counts "   " as
  // three characters and happily stores a form with no readable name at all.
  title: z.string().trim().min(1).max(300),
  slug: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9][a-z0-9-]*$/, "Use lowercase letters, numbers and hyphens"),
  description: z.string().max(2000).optional(),
  brandProfileId: z.string().nullable().optional(),
  logoLayout: z.enum(["side_by_side", "stacked", "primary_only", "secondary_only"]).optional(),
});

export const formUpdateRequestSchema = z.object({
  title: z.string().trim().min(1).max(300).optional(),
  internalName: z.string().trim().max(300).nullable().optional(),
  description: z.string().max(2000).nullable().optional(),
  requiresIdentity: z.boolean().optional(),
  allowDrafts: z.boolean().optional(),
  draftExpiryDays: z.number().int().min(1).max(365).optional(),
  allowEditAfterSubmit: z.boolean().optional(),
  editWindowHours: z.number().int().min(1).nullable().optional(),
  turnstileEnabled: z.boolean().optional(),
  captureGeo: z.boolean().optional(),
  reviewBeforeSubmit: z.boolean().optional(),
  lawfulBasis: z.enum(["consent", "contract", "legitimate_interest"]).optional(),
  brandProfileId: z.string().nullable().optional(),
  themeOverrides: z.record(z.unknown()).nullable().optional(),
});

/**
 * Clone a form — its questions, logic, branding and rules — into a new form with no submissions.
 *
 * The headline case is the annual roll-forward into next season's cycle (docs/03-API-SPEC.md §5),
 * but `targetCycleId` is optional and omitting it keeps the source's own cycle: building this
 * year's Edo form from this year's Delta one is the same job, and refusing it would mean rebuilding
 * sixty fields by hand to change a title.
 */
export const formDuplicateRequestSchema = z.object({
  targetCycleId: z.string().min(1).nullish(),
  newTitle: z.string().trim().min(1).max(300),
  newSlug: z
    .string()
    .min(1)
    .max(120)
    .regex(/^[a-z0-9][a-z0-9-]*$/, "Use lowercase letters, numbers and hyphens"),
});

export type DraftField = z.infer<typeof draftFieldSchema>;
export type DraftSection = z.infer<typeof draftSectionSchema>;
export type DraftSaveRequest = z.infer<typeof draftSaveRequestSchema>;
export type FormCreateRequest = z.infer<typeof formCreateRequestSchema>;
export type FormUpdateRequest = z.infer<typeof formUpdateRequestSchema>;
export type FormDuplicateRequest = z.infer<typeof formDuplicateRequestSchema>;

/** One problem found by `POST /forms/:id/draft/validate`. */
export interface DraftValidationIssue {
  code: string;
  message: string;
  fieldKey?: string;
  ruleId?: string;
}
